import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Pill, Package, Hash, RefreshCw } from 'lucide-react';

interface Medication {
  id: string;
  medication: string;
  dose: string;
  quantity_authorized: string;
  unit_of_measure: string;
  sig: string;
  refills_authorized: string;
  price: number;
  product_id: string;
}

interface MedicationCardProps {
  medication: Medication;
  isSelected: boolean;
  onToggle: (selected: boolean) => void;
  index?: number;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const MedicationCard: React.FC<MedicationCardProps> = ({ 
  medication, 
  isSelected, 
  onToggle,
  index = 0 
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
    >
      <Card className={`transition-all duration-200 ${
        isSelected 
          ? 'border-blue-500 bg-blue-50/50 shadow-md' 
          : 'border-gray-200 hover:border-gray-300'
      }`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className="pt-1">
              <Checkbox
                checked={isSelected}
                onCheckedChange={onToggle}
                className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
              />
            </div>
            
            <div className="flex-1 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Pill className="h-4 w-4 text-blue-600" />
                    {medication.medication}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {medication.dose} - {medication.quantity_authorized} {medication.unit_of_measure}
                  </p>
                </div>
                
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(medication.price)}
                  </p>
                  <p className="text-xs text-gray-500">per consultation</p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Instructions:</span> {medication.sig}
                </p>
              </div>
              
              <div className="flex items-center gap-4 text-sm">
                <Badge variant="outline" className="gap-1">
                  <Package className="h-3 w-3" />
                  Qty: {medication.quantity_authorized}
                </Badge>
                <Badge variant="outline" className="gap-1">
                  <RefreshCw className="h-3 w-3" />
                  Refills: {medication.refills_authorized}
                </Badge>
                <Badge variant="outline" className="gap-1">
                  <Hash className="h-3 w-3" />
                  ID: {medication.product_id}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default MedicationCard; 